import { makeValuesPredicate } from '@sgme/fp';
import { AddonAction } from 'models/AddonAction';
import { NotionalType } from 'models/common/notional';
import type { Custodian } from 'models/custodian';
import type { AddonSolvingMode, LoadingResult, MergeDeal } from 'models/forms/AddonFormResult';
import type { DealSplitData } from 'models/forms/DealSplitData';
import { SalesBeneficiary } from 'models/forms/SalesBeneficiary';
import { Item } from 'models/IItem';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { QuoteFormError } from 'state/addonForm/addonForm.selector';
import type { QuoteDetailsData } from 'state/quotes/details/details.model';
import type { QuoteState } from 'state/quotes/infos/infos.model';
import type { BackToBackType } from 'state/uiPanelState/tradeConfirmation/tradeConfirm.model';
import type { Collection } from 'state/utils/types';

export type SolveMode = AddonSolvingMode | 'UpfrontFee' | 'OfferPrice' | 'FixedAmount';

export type TradeClientSide = 'buy' | 'sell';
const remunerationModes = ['Upfront', 'Reoffer', 'Premium'] as const;
export const isRemunerationMode = makeValuesPredicate(remunerationModes);
export type RemunerationMode = (typeof remunerationModes)[number];
export type InitialTradeSide = 'Buy' | 'Sell';

export interface AddonFormTabInfo {
  productId: string;
  instrumentName: string;
  instrumentContractName: string;
  wrapper: string;
  fileCode?: string;
  isinCode?: string;
  isOtc: boolean;
  isShareAccDec: boolean;
  assetTypes?: string[];
  originalQuoteId: string;
  currency: string;
  quoteState?: QuoteState;
  isPriceInconsistent?: boolean;
  initialTradeSide?: InitialTradeSide;
  isBackToBack?: boolean;
  backToBackType?: string;
  isDelayedBackToBack?: boolean;
}

export interface AddonFormTabData {
  availableSolvingModes: readonly AddonSolvingMode[];
  backToBackFileCode?: string;
  backToBackIsBackToBack?: boolean;
  backToBackIsDelayedBackToBack?: boolean;
  backToBackIsDeltaAdjusted?: boolean;
  backToBackNoteNominal?: number;
  backToBackType?: BackToBackType;
  addonAction?: AddonAction;
  tradeClientSide?: TradeClientSide;
  selectedOnBehalfClient: ISalesCounterpart | null;
  custodians: LoadingResult<ReadonlyArray<Custodian>>;
  mergeDealsRequested: boolean;
  mergeableDeals?: LoadingResult<ReadonlyArray<MergeDeal>>;
  selectedTradeReference?: string;
  addonNotional?: number;
  addonNotionalInUnits?: number;
  notionalType: NotionalType;
  totalNumberOfAccumulationDays?: number;
  dailyNumberOfShares?: number;
  dealSplit: readonly DealSplitData[];
  remunerationMode: RemunerationMode;
  solveModes: readonly SolveMode[];
  solveFor: SolveMode;
  xMargin?: number;
  upFrontFee?: number;
  offerPrice?: number;
  fixedAmount?: number;
  fundingLockDate?: string;
  lastQuoteId?: string;
  lastQuoteExternalId?: string;
  isReadOnly?: boolean;
  tradeRejectionValidationErrors?: readonly QuoteFormError[];
  tradeDoneExecMessages?: readonly QuoteFormError[];
  addonPrimaryDealId?: string;
  salesBeneficiary?: string;
  salesDesk?: number;
  availableSalesBeneficiaries?: Item<SalesBeneficiary>[];
  availableSalesDesks?: Item<number>[];
}

export type AddonFormLoadingStatus = 'Pending' | 'Success' | 'Error' | 'Resetting';
export type AddonFormTabState = {
  form?: AddonFormTabData;
  info?: AddonFormTabInfo;
  quoteDetails?: Readonly<QuoteDetailsData>;

  loadingError?: string;
  loadingStatus: AddonFormLoadingStatus;
};

export type AddonFormState = Collection<AddonFormTabState>;

export const initialAddonFormState: AddonFormState = {};
export const defaultAddonSolvingModes: readonly AddonSolvingMode[] = ['XMargin', 'Price'];
